import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import {
  ArrowLeft, Loader2, Eye, EyeOff, Lock,
  CheckCircle, AlertCircle, Shield, Image as ImageIcon
} from 'lucide-react';

type UserType = 'consultant' | 'client' | 'admin';
type Step = 'loading' | 'password' | 'totp' | 'success' | 'error';

function getLoginPath(userType: UserType): string {
  if (userType === 'consultant') return '/consultant/login';
  if (userType === 'client') return '/customer/login';
  return '/admin/login';
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userType = (searchParams.get('type') as UserType) || 'consultant';

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [step, setStep] = useState<Step>('loading');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpError, setTotpError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifyingTotp, setIsVerifyingTotp] = useState(false);
  const [sessionToken, setSessionToken] = useState('');
  const [has2FA, setHas2FA] = useState(false);

  useEffect(() => {
    fetchLogo();
    handleRecoveryToken();
  }, []);

  const handleRecoveryToken = async () => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token') || '';
    const tokenType = params.get('type');

    if (!accessToken || tokenType !== 'recovery') {
      setStep('error');
      return;
    }

    try {
      const { data, error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (error || !data.session) {
        setStep('error');
        return;
      }

      setSessionToken(data.session.access_token);

      const userId = data.session.user.id;
      const enabled = await check2FAEnabled(userId, userType);
      setHas2FA(enabled);
      setStep('password');
    } catch {
      setStep('error');
    }
  };

  const check2FAEnabled = async (userId: string, type: UserType): Promise<boolean> => {
    try {
      if (type === 'consultant') {
        const { data } = await supabase
          .from('consultant_profiles')
          .select('is_2fa_enabled')
          .eq('id', userId)
          .maybeSingle();
        return data?.is_2fa_enabled || false;
      } else if (type === 'client') {
        const { data } = await supabase
          .from('workday_client_profiles')
          .select('is_2fa_enabled')
          .eq('id', userId)
          .maybeSingle();
        return data?.is_2fa_enabled || false;
      } else if (type === 'admin') {
        const { data } = await supabase
          .from('user_roles')
          .select('is_2fa_enabled')
          .eq('user_id', userId)
          .maybeSingle();
        return data?.is_2fa_enabled || false;
      }
    } catch {
      return false;
    }
    return false;
  };

  const fetchLogo = async () => {
    try {
      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);

      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'logo_url')
        .maybeSingle();

      if (!error && data?.value) {
        const img = new Image();
        img.onload = () => { setLogoUrl(data.value); setLogoLoading(false); };
        img.onerror = () => setLogoLoading(false);
        img.src = data.value;
      } else {
        setLogoLoading(false);
      }
    } catch {
      setLogoLoading(false);
    }
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    if (has2FA) {
      setStep('totp');
    } else {
      performPasswordReset();
    }
  };

  const handleTotpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setTotpError('');

    if (totpCode.length !== 6) {
      setTotpError('Please enter a 6-digit code.');
      return;
    }

    setIsVerifyingTotp(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || sessionToken;

      if (!token) {
        setTotpError('Session expired. Please request a new reset link.');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twofa-verify`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ token: totpCode, enable: false }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setTotpError(data.error || 'Invalid authentication code. Please try again.');
        return;
      }

      await performPasswordReset();
    } catch {
      setTotpError('Failed to verify code. Please try again.');
    } finally {
      setIsVerifyingTotp(false);
    }
  };

  const performPasswordReset = async () => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setStep('success');
      setTimeout(() => navigate(getLoginPath(userType)), 3000);
    } catch (err: any) {
      setTotpError(err.message || 'Failed to reset password. Please try again.');
      setPasswordError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTotpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setTotpCode(value);
      setTotpError('');
    }
  };

  const isClientTheme = userType === 'client' || userType === 'admin';
  const bg = isClientTheme ? 'bg-gray-50' : 'bg-gray-900';
  const cardBg = isClientTheme ? 'bg-white border-gray-200 shadow-lg' : 'bg-gray-800 border-gray-700';
  const textPrimary = isClientTheme ? 'text-gray-900' : 'text-white';
  const textSecondary = isClientTheme ? 'text-gray-600' : 'text-gray-400';
  const textMuted = isClientTheme ? 'text-gray-400' : 'text-gray-500';
  const inputBg = isClientTheme ? 'bg-white border-gray-300 text-gray-900' : 'bg-gray-700 border-gray-600 text-white';
  const backBtn = isClientTheme ? 'text-gray-600 hover:text-gray-900' : 'text-gray-400 hover:text-white';
  const navBg = isClientTheme ? 'bg-white border-b border-gray-200' : 'bg-black/80 backdrop-blur-md';
  const errorBg = isClientTheme ? 'bg-red-50 border-red-200' : 'bg-red-500/10 border-red-500/50';
  const errorText = isClientTheme ? 'text-red-600' : 'text-red-400';
  const errorIcon = isClientTheme ? 'text-red-500' : 'text-red-500';
  const inputIcon = isClientTheme ? 'text-gray-400' : 'text-gray-400';
  const eyeBtn = isClientTheme ? 'text-gray-400 hover:text-gray-600' : 'text-gray-400 hover:text-white';
  const divider = isClientTheme ? 'border-gray-200' : 'border-gray-700';
  const supportLink = isClientTheme ? 'text-blue-600' : 'text-blue-400';

  if (step === 'loading') {
    return (
      <div className={`min-h-screen ${bg} flex items-center justify-center`}>
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className={`min-h-screen ${bg} ${textPrimary} flex items-center justify-center px-4`}>
        <div className="max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-4">Invalid Reset Link</h1>
          <p className={`${textSecondary} mb-6`}>
            This password reset link is invalid or has expired. Please request a new one from the login page.
          </p>
          <button
            onClick={() => navigate(getLoginPath(userType))}
            className="bg-primary text-black font-semibold px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bg} ${textPrimary} flex flex-col`}>
      <nav className={`fixed w-full z-50 ${navBg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <button
              onClick={() => navigate('/')}
              className={`${textPrimary} hover:text-primary transition-colors -mt-4 -mb-4`}
            >
              {logoLoading ? (
                <div className="h-32 md:h-28 w-[200px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : logoUrl ? (
                <img src={logoUrl} alt="Logo" className="h-32 md:h-28 w-auto object-contain max-w-[500px]" />
              ) : (
                <ImageIcon size={32} className="text-primary" />
              )}
            </button>
          </div>
        </div>
      </nav>

      <div className="flex-1 flex items-center justify-center px-4 pt-32 pb-12">
        <div className="max-w-md w-full">
          {step !== 'success' && (
            <button
              onClick={() => {
                if (step === 'totp') setStep('password');
                else navigate(getLoginPath(userType));
              }}
              className={`flex items-center ${backBtn} transition-colors mb-8`}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {step === 'totp' ? 'Back to Password' : 'Back to Login'}
            </button>
          )}

          <div className={`rounded-xl p-8 border ${cardBg}`}>

            {step === 'success' && (
              <div className="text-center">
                <div className={`inline-flex items-center justify-center w-20 h-20 ${isClientTheme ? 'bg-green-100' : 'bg-green-500/20'} rounded-full mb-6`}>
                  <CheckCircle className={`w-10 h-10 ${isClientTheme ? 'text-green-600' : 'text-green-400'}`} />
                </div>
                <h2 className="text-2xl font-bold mb-3">Password Reset Successful</h2>
                <p className={`${textSecondary} mb-2`}>Your password has been successfully reset.</p>
                <p className={`text-sm ${textMuted}`}>Redirecting to login...</p>
              </div>
            )}

            {step === 'password' && (
              <>
                <h2 className="text-2xl font-bold mb-1">Reset Your Password</h2>
                <p className={`${textSecondary} mb-6 text-sm`}>
                  {has2FA
                    ? 'Enter your new password below. You will need to verify with your authenticator app next.'
                    : 'Enter your new password below.'}
                </p>

                {passwordError && (
                  <div className={`mb-5 p-4 ${errorBg} border rounded-lg flex items-start gap-3`}>
                    <AlertCircle className={`w-5 h-5 ${errorIcon} flex-shrink-0 mt-0.5`} />
                    <p className={`text-sm ${errorText}`}>{passwordError}</p>
                  </div>
                )}

                <form onSubmit={handlePasswordSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium mb-2">New Password</label>
                    <div className="relative">
                      <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${inputIcon} w-5 h-5`} />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${inputBg}`}
                        placeholder="Enter new password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${eyeBtn}`}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className={`text-xs ${textMuted} mt-1`}>Must be at least 8 characters</p>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">Confirm New Password</label>
                    <div className="relative">
                      <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${inputIcon} w-5 h-5`} />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${inputBg}`}
                        placeholder="Confirm new password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${eyeBtn}`}
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary text-black font-bold py-3 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Resetting Password...
                      </>
                    ) : has2FA ? (
                      'Verify Account'
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </form>
              </>
            )}

            {step === 'totp' && (
              <>
                <div className="text-center mb-6">
                  <div className={`inline-flex items-center justify-center w-16 h-16 ${isClientTheme ? 'bg-blue-50' : 'bg-blue-500/20'} rounded-full mb-4`}>
                    <Shield className={`w-8 h-8 ${isClientTheme ? 'text-blue-600' : 'text-blue-400'}`} />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Verify Your Identity</h2>
                  <p className={`${textSecondary} text-sm`}>Enter the 6-digit code from your authenticator app to complete the password reset.</p>
                </div>

                {totpError && (
                  <div className={`mb-5 p-4 ${errorBg} border rounded-lg flex items-start gap-3`}>
                    <AlertCircle className={`w-5 h-5 ${errorIcon} flex-shrink-0 mt-0.5`} />
                    <p className={`text-sm ${errorText}`}>{totpError}</p>
                  </div>
                )}

                <form onSubmit={handleTotpVerify} className="space-y-5">
                  <div>
                    <label htmlFor="totpCode" className="block text-sm font-medium mb-2 text-center">Authentication Code</label>
                    <input
                      type="text"
                      id="totpCode"
                      value={totpCode}
                      onChange={handleTotpChange}
                      placeholder="000000"
                      className={`w-full px-4 py-4 border rounded-lg text-center text-3xl tracking-widest font-mono focus:ring-2 focus:ring-primary focus:border-transparent ${isClientTheme ? 'bg-white border-gray-300 text-gray-900 placeholder-gray-300' : 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'}`}
                      maxLength={6}
                      autoComplete="off"
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isVerifyingTotp || isSubmitting || totpCode.length !== 6}
                    className="w-full bg-primary text-black font-bold py-3 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isVerifyingTotp || isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {isSubmitting ? 'Resetting Password...' : 'Verifying...'}
                      </>
                    ) : (
                      'Verify & Reset Password'
                    )}
                  </button>
                </form>

                <div className={`mt-6 pt-5 border-t ${divider}`}>
                  <p className={`text-sm ${textMuted} text-center`}>
                    Lost access to your authenticator app?{' '}
                    <a href="/request-access" className={`${supportLink} hover:underline font-medium`}>Contact support</a>
                  </p>
                </div>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
